import React, { useMemo, memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Link } from 'react-router-dom'

import Text from '../../../../ui/Text'
import { WidgetLayout } from '../../../../lib/widget-layout'
import { dateLocalize } from '../../../../lib/date-localize'
import { isSupportDomain } from '../../../../lib/currentDomain'
import { useLoadBalancerGroupGetByIdQuery, MeasurementDeviceExtendedGetModel } from '../../../redux/api/web-api'
import { getProductName } from '../../../services/getProductName'
import { Table, TableRow, TableBody, TableBodyCell } from '../../../../lib/table'
import useText from '../../../hooks/useText'

export type MeasurementDevicePropertiesWidgetProps = {
    device?: MeasurementDeviceExtendedGetModel
    className?: ClassName
    style?: React.CSSProperties
}

export const MeasurementDevicePropertiesWidget = memo(function MeasurementDevicePropertiesWidget({
    device,
    className,
    style,
}: MeasurementDevicePropertiesWidgetProps): React.ReactElement {
    const text = useText()
    const { data: loadBalancerGroup } = useLoadBalancerGroupGetByIdQuery(
        { id: device?.loadBalancerGroupId || 0 },
        {
            skip: !device?.loadBalancerGroupId,
        }
    )

    const productName = useMemo(() => {
        if (!device) return `${text('msgLoading').toLocaleLowerCase()}...`

        const name = getProductName(device?.productId)
        return name || '---'
    }, [device])

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblPropertiesWidget')}
            data-testid="properties"
        >
            <Table>
                <TableBody>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-serial">
                            <Text code="lblSerialNumber" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-serial-">
                            {device?.serialNumber || '---'}
                        </TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-password">
                            <Text code="lblPassword" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-password">{device?.password || '---'}</TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-created">
                            <Text code="lblCreated" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-created">
                            {dateLocalize(device?.created || null)}
                        </TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-org">
                            <Text code="lblOrganisation" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-org">
                            {device?.companyName && isSupportDomain ? (
                                <Link to={`/company/${device?.companyId}`} className="btn-link">
                                    {device?.companyName}
                                </Link>
                            ) : (
                                device?.companyName || '---'
                            )}
                        </TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-manufacture">
                            <Text code="lblManufacturer" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-manufacture">
                            {device?.manufacturer || '---'}
                        </TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-product">
                            <Text code="lblProduct" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-product">{productName}</TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-lb">
                            <Text code="lblLoadbalancerGroup" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-lb">
                            {device?.loadBalancerGroupName && device?.loadBalancerGroupId ? (
                                <Link
                                    to={`/loadbalancer/${device?.loadBalancerGroupId}`}
                                    className="btn-link"
                                    data-testid="lb-link"
                                >
                                    {device?.loadBalancerGroupName}
                                </Link>
                            ) : (
                                '---'
                            )}
                        </TableBodyCell>
                    </TableRow>
                    <TableRow>
                        <TableBodyCell data-testid="table-TableBodyCell-supply">
                            <Text code="lblSupplyMaxCurrent" />:
                        </TableBodyCell>
                        <TableBodyCell data-testid="table-TableRow-supply">
                            <span>
                                {loadBalancerGroup?.supplyMaxCurrent
                                    ? `${loadBalancerGroup?.supplyMaxCurrent} ${text('lblAmpereShort')}`
                                    : '---'}
                            </span>
                        </TableBodyCell>
                    </TableRow>
                </TableBody>
            </Table>
        </WidgetLayout>
    )
})
